import { useQuery } from '@tanstack/react-query';
import type { ExternalTestHealth, ExternalTestVerdict } from '@chatsdet/types';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';
import type { ExternalRunDetailDto } from './useExternalRun';

export type ExternalScenarioHistoryRunDto = {
  runId: string;
  /** A real item in this scenario, used to address its detail route. */
  itemId: string;
  runStatus: ExternalRunDetailDto['status'];
  verdict: ExternalTestVerdict;
  createdAt: string;
  /** The failure inherited an earlier run's diagnosis instead of a fresh one. */
  repeatedDiagnosis: boolean;
};

export type ExternalScenarioDetailDto = ExternalRunDetailDto & {
  /** The area (shard) that ran this test; null for a pre-area run. */
  area: { index: number; label: string } | null;
  history: {
    runs: ExternalScenarioHistoryRunDto[];
    health: ExternalTestHealth;
  };
};

/**
 * Fetch one logical scenario, not every raw item in its suite run. Poll while
 * the run is live so newly reported attempts and the history strip converge.
 */
export function useExternalScenarioDetail(runId: string | null, itemId: string | null) {
  return useQuery<ExternalScenarioDetailDto>({
    queryKey: ['external-scenario-detail', runId, itemId],
    enabled: Boolean(runId && itemId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return (status && status !== 'sealed' && status !== 'canceled') ||
        query.state.data?.recordings?.status === 'preparing'
        ? 3000
        : false;
    },
    queryFn: async () => {
      const res = await fetch(`${API_URL}/external-tests/runs/${runId}/items/${itemId}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to load external test');
      const body = (await res.json()) as { ok: boolean; data: ExternalScenarioDetailDto };
      return body.data;
    },
  });
}
